import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Swal from "sweetalert2";
import {
  Star,
  Clock,
  ChevronLeft,
  ChevronRight,
  ShoppingBag,
  Calendar,
  CreditCard,
  Package,
  CheckCircle,
  XCircle,
  Truck,
  AlertCircle,
  ThumbsUp,
} from "lucide-react";

const ITEMS_PER_PAGE = 6;

// Animated Star Rating
function StarRating({ rating, setRating, readonly = false }) {
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <motion.button
          key={star}
          whileHover={readonly ? {} : { scale: 1.2 }}
          whileTap={readonly ? {} : { scale: 0.9 }}
          onClick={() => !readonly && setRating(star)}
          className={`text-2xl transition-colors ${
            star <= rating ? "text-yellow-400" : "text-gray-300"
          } ${readonly ? "cursor-default" : "cursor-pointer"}`}
        >
          ★
        </motion.button>
      ))}
    </div>
  );
}

// Status Badge Component
function StatusBadge({ status }) {
  const getStatusConfig = (status) => {
    switch (status?.toLowerCase()) {
      case "delivered":
        return { color: "bg-green-100 text-green-700 border-green-200", icon: <CheckCircle size={14} /> };
      case "shipped":
        return { color: "bg-blue-100 text-blue-700 border-blue-200", icon: <Truck size={14} /> };
      case "processing":
        return { color: "bg-yellow-100 text-yellow-700 border-yellow-200", icon: <Package size={14} /> };
      case "cancelled":
        return { color: "bg-red-100 text-red-700 border-red-200", icon: <XCircle size={14} /> };
      default:
        return { color: "bg-gray-100 text-gray-700 border-gray-200", icon: <Clock size={14} /> };
    }
  };

  const config = getStatusConfig(status);

  return (
    <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium border ${config.color}`}>
      {config.icon}
      {status || "Pending"}
    </span>
  );
}

// Countdown Timer
function CountdownTimer({ placedAt }) {
  const [timeLeft, setTimeLeft] = useState("");

  useEffect(() => {
    const calculateTimeLeft = () => {
      const endTime = new Date(placedAt);
      endTime.setDate(endTime.getDate() + 7);
      const now = new Date();
      const diff = endTime - now;

      if (diff <= 0) return "Expired";

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
      const minutes = Math.floor((diff / (1000 * 60)) % 60);
      const seconds = Math.floor((diff / 1000) % 60);

      return `${days}d ${hours}h ${minutes}m ${seconds}s`;
    };

    setTimeLeft(calculateTimeLeft());
    const interval = setInterval(() => setTimeLeft(calculateTimeLeft()), 1000);
    return () => clearInterval(interval);
  }, [placedAt]);

  return (
    <div className="flex items-center gap-2">
      <Clock size={16} className="text-orange-500" />
      <span className="font-mono font-semibold text-orange-600">{timeLeft}</span>
    </div>
  );
}

// Product Card
function ProductCard({ order, onReview }) {
  const isExpired = order.placedAt && new Date(new Date(order.placedAt).getTime() + 7 * 24 * 60 * 60 * 1000) < new Date();

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -5, scale: 1.02 }}
      className="bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden hover:shadow-xl transition-all duration-300"
    >
      {/* Image */}
      <div className="relative h-48 overflow-hidden">
        <img
          src={order.thumbnail || "https://via.placeholder.com/400x250?text=No+Image"}
          alt={order.item}
          className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
        />
        <div className="absolute top-3 left-3">
          <StatusBadge status={order.status} />
        </div>
        <div className="absolute top-3 right-3">
          <span className="px-2 py-1 bg-white/90 backdrop-blur rounded-lg text-xs font-medium text-gray-700">
            {order.qty} ×
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="p-5">
        <h3 className="font-semibold text-lg text-gray-800 mb-3 line-clamp-1">{order.item}</h3>

        {/* Order Info */}
        <div className="space-y-2 mb-4">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-500 flex items-center gap-2">
              <CreditCard size={14} /> Order ID
            </span>
            <span className="font-mono text-gray-700">{order.id}</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-500 flex items-center gap-2">
              <ShoppingBag size={14} /> Amount
            </span>
            <span className="font-bold text-gray-800">₹{order.amount}</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-500 flex items-center gap-2">
              <Calendar size={14} /> Date
            </span>
            <span className="text-gray-700">{order.date}</span>
          </div>
        </div>

        {/* Countdown */}
        {!isExpired && order.placedAt && (
          <div className="p-3 bg-orange-50 rounded-xl mb-4">
            <CountdownTimer placedAt={order.placedAt} />
          </div>
        )}

        {/* Review Button */}
        <motion.button
          whileTap={{ scale: 0.98 }}
          onClick={() => onReview(order)}
          className="w-full py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-medium shadow-lg shadow-blue-500/30 hover:shadow-xl transition-all flex items-center justify-center gap-2"
        >
          <ThumbsUp size={18} />
          Write Review
        </motion.button>
      </div>
    </motion.div>
  );
}

// Review Modal
function ReviewModal({ show, onClose, product, onSubmit, loading }) {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");

  React.useEffect(() => {
    if (show) {
      setRating(0);
      setComment("");
    }
  }, [show]);

  const handleSubmit = () => {
    if (!rating) {
      Swal.fire({
        icon: "warning",
        title: "Rating Required",
        text: "Please select at least 1 star ⭐",
        background: "#fff",
        confirmButtonColor: "#3b82f6",
      });
      return;
    }
    onSubmit(rating, comment);
  };

  if (!show || !product) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 text-white">
          <h2 className="text-xl font-bold">Write a Review</h2>
          <p className="text-white/80 text-sm mt-1">Share your experience</p>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Product Info */}
          <div className="flex items-center gap-4 mb-6 p-4 bg-gray-50 rounded-xl">
            <img
              src={product.thumbnail || "https://via.placeholder.com/80"}
              alt={product.item}
              className="w-16 h-16 rounded-lg object-cover"
            />
            <div className="flex-1">
              <h3 className="font-semibold text-gray-800 line-clamp-1">{product.item}</h3>
              <p className="text-sm text-gray-500">Order ID: {product.id}</p>
            </div>
          </div>

          {/* Rating */}
          <div className="text-center mb-6">
            <p className="text-sm text-gray-500 mb-3">How would you rate this product?</p>
            <StarRating rating={rating} setRating={setRating} />
            <p className="text-xs text-gray-400 mt-2">
              {rating === 1 ? "Poor" : rating === 2 ? "Fair" : rating === 3 ? "Good" : rating === 4 ? "Very Good" : rating === 5 ? "Excellent" : "Select rating"}
            </p>
          </div>

          {/* Comment */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Your Review</label>
            <textarea
              rows="4"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Tell us what you liked or didn't like..."
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-all resize-none"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 py-3 border border-gray-200 rounded-xl font-medium text-gray-600 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="flex-1 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-medium shadow-lg shadow-blue-500/30 hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Submitting...
                </span>
              ) : (
                "Submit Review"
              )}
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

// Pagination Component
function Pagination({ currentPage, totalPages, onPageChange }) {
  const pages = [...Array(totalPages)].map((_, i) => i + 1);

  return (
    <div className="flex items-center justify-center gap-2 mt-8">
      <motion.button
        whileTap={{ scale: 0.95 }}
        onClick={() => onPageChange(Math.max(1, currentPage - 1))}
        disabled={currentPage === 1}
        className="p-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
      >
        <ChevronLeft size={20} />
      </motion.button>

      <div className="flex gap-1">
        {pages.map((page) => (
          <motion.button
            key={page}
            whileTap={{ scale: 0.95 }}
            onClick={() => onPageChange(page)}
            className={`w-10 h-10 rounded-lg font-medium transition-all ${
              currentPage === page
                ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg shadow-blue-500/30"
                : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-50"
            }`}
          >
            {page}
          </motion.button>
        ))}
      </div>

      <motion.button
        whileTap={{ scale: 0.95 }}
        onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
        disabled={currentPage === totalPages}
        className="p-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
      >
        <ChevronRight size={20} />
      </motion.button>
    </div>
  );
}

const Products = () => {
  const orders = useSelector((state) => state.orders.list);
  const [currentPage, setCurrentPage] = useState(1);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Authentication check - redirect to login if not logged in
  useEffect(() => {
    const user = localStorage.getItem("user");
    if (!user) {
      navigate("/login");
    }
  }, [navigate]);

  // Clear orders if not logged in
  useEffect(() => {
    const user = localStorage.getItem("user");
    if (!user && orders.length > 0) {
      // Orders will be cleared from Redux on logout
    }
  }, [orders.length]);

  const submitReview = async (rating, comment) => {
    try {
      setLoading(true);

      await fetch("https://localhost:44370/api/Product/review", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          productId: selectedProduct.productId,
          rating,
          comment,
        }),
      });

      Swal.fire({
        icon: "success",
        title: "Review Submitted!",
        text: "Thank you for your feedback 🙌",
        timer: 2000,
        showConfirmButton: false,
        background: "#fff",
        confirmButtonColor: "#3b82f6",
      });

      setShowReviewModal(false);
    } catch (err) {
      console.error(err);

      Swal.fire({
        icon: "error",
        title: "Submission Failed",
        text: "Something went wrong. Please try again.",
        background: "#fff",
        confirmButtonColor: "#3b82f6",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleReview = (order) => {
    setSelectedProduct(order);
    setShowReviewModal(true);
  };

  // Pagination
  const totalPages = Math.ceil(orders.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const currentOrders = orders.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  if (orders.length === 0) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="text-center"
        >
          <div className="w-24 h-24 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
            <ShoppingBag className="w-12 h-12 text-gray-400" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">No Products Yet</h2>
          <p className="text-gray-500 mb-6">You haven't purchased any products yet.</p>
          <button
            onClick={() => window.history.back()}
            className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-medium shadow-lg shadow-blue-500/30"
          >
            Browse Products
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <h2 className="text-3xl font-bold text-gray-800">My Purchases</h2>
        <p className="text-gray-500 mt-1">Manage and review your orders</p>
      </motion.div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        {[
          { label: "Total Orders", value: orders.length, icon: <ShoppingBag size={20} />, color: "from-blue-500 to-cyan-500" },
          { label: "Delivered", value: orders.filter(o => o.status?.toLowerCase() === "delivered").length, icon: <CheckCircle size={20} />, color: "from-green-500 to-emerald-500" },
          { label: "Pending", value: orders.filter(o => o.status?.toLowerCase() !== "delivered").length, icon: <Clock size={20} />, color: "from-orange-500 to-yellow-500" },
        ].map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white rounded-2xl p-5 shadow-md border border-gray-100"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm">{stat.label}</p>
                <p className="text-3xl font-bold text-gray-800 mt-1">{stat.value}</p>
              </div>
              <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${stat.color} flex items-center justify-center text-white`}>
                {stat.icon}
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Products Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <AnimatePresence>
          {currentOrders.map((order, index) => (
            <ProductCard key={order.id} order={order} onReview={handleReview} />
          ))}
        </AnimatePresence>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      )}

      {/* Review Modal */}
      <ReviewModal
        show={showReviewModal}
        onClose={() => setShowReviewModal(false)}
        product={selectedProduct}
        onSubmit={submitReview}
        loading={loading}
      />
    </div>
  );
};

export default Products;
